import { debounce } from "lodash"
import * as React from "react"
import { Control, Emitter } from "rete"
import { EventsTypes } from "rete/types/events"
import {
  downloadTableSpreadsheetCsv,
  downloadTableSpreadsheetXlsx,
  mergeImportedTable,
  parseTableSpreadsheetCsv,
  parseTableSpreadsheetXlsx,
  type TableSpreadsheetData,
} from "../table_spreadsheet"

export const TABLE_ROWS_KEY = "tableRows"
export const TABLE_COLUMNS_KEY = "tableColumns"
export const TABLE_CELLS_KEY = "tableCells"
export const TABLE_OPEN_KEY = "tableOpen"

export const NEW_TABLE_COMPONENT_NAME = "New Table"

/** UI-only flag — do not persist; tables should always load collapsed. */
export function stripTransientTableNodeData(model: { nodes?: Record<string, { name?: string; data?: Record<string, unknown> }> } | null): void {
  if (!model?.nodes) return
  for (const id of Object.keys(model.nodes)) {
    const node = model.nodes[id]
    if (node?.name === NEW_TABLE_COMPONENT_NAME && node.data) {
      delete node.data[TABLE_OPEN_KEY]
    }
  }
}

export type TableCell = number | null

interface TableEditorProps {
  getRows: () => string[]
  getColumns: () => string[]
  getCells: () => TableCell[][]
  getOpen: () => boolean
  setOpen: (open: boolean) => void
  /** True when columns are driven by a connected reference dataset (read-only). */
  getColumnsLocked: () => boolean
  setRowName: (index: number, name: string) => void
  setColumnName: (index: number, name: string) => void
  setCell: (rowIndex: number, columnIndex: number, value: string) => void
  addRow: () => void
  addColumn: () => void
  removeRow: (index: number) => void
  removeColumn: (index: number) => void
  importTable: (data: TableSpreadsheetData) => void
  getExportFilename: () => string
}

function cellToString(value: TableCell): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : ""
}

const TableEditorView = (props: TableEditorProps) => {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [hoverCell, setHoverCell] = React.useState<{ r: number; c: number } | null>(null)

  const open = props.getOpen()
  const rows = props.getRows()
  const columns = props.getColumns()
  const cells = props.getCells()
  const columnsLocked = props.getColumnsLocked()

  const stop = (e: React.SyntheticEvent) => e.stopPropagation()

  const isRowHighlightCell = (r: number, c: number) =>
    hoverCell !== null && hoverCell.r === r && c <= hoverCell.c

  const isColHighlightCell = (r: number, c: number) =>
    hoverCell !== null && hoverCell.c === c && r <= hoverCell.r

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const isExcel = /\.xlsx?$/i.test(file.name)

    if (isExcel) {
      const reader = new FileReader()
      reader.onload = () => {
        const buffer = reader.result
        if (!(buffer instanceof ArrayBuffer)) return
        const parsed = parseTableSpreadsheetXlsx(buffer)
        if (parsed) {
          props.importTable(parsed)
          forceUpdate()
        }
        if (fileInputRef.current) fileInputRef.current.value = ""
      }
      reader.readAsArrayBuffer(file)
    } else {
      const reader = new FileReader()
      reader.onload = () => {
        const text = String(reader.result ?? "")
        const parsed = parseTableSpreadsheetCsv(text)
        if (parsed) {
          props.importTable(parsed)
          forceUpdate()
        }
        if (fileInputRef.current) fileInputRef.current.value = ""
      }
      reader.readAsText(file)
    }
  }

  return (
    <div
      className={`table-editor${open ? " table-editor--open" : ""}${
        columnsLocked ? " table-editor--columns-locked" : ""
      }`}
      onPointerDown={stop}
      onDoubleClick={stop}
    >
      <button
        type="button"
        className={`btn table-editor__toggle${open ? " table-editor__toggle--open" : ""}`}
        onPointerDown={stop}
        onClick={e => {
          stop(e)
          const next = !props.getOpen()
          props.setOpen(next)
          forceUpdate()
        }}
      >
        <i className={`fa fa-table mr-1`} aria-hidden="true" />
        {open ? "Hide table" : "Show table"}
        <span className="table-editor__dims">{`${rows.length}\u00d7${columns.length}`}</span>
      </button>

      {open && (
        <div className="table-editor__body">
          <div className="table-editor__toolbar">
            <button
              type="button"
              className="btn btn-sm table-editor__io table-editor__io--import"
              onPointerDown={stop}
              onClick={e => {
                stop(e)
                fileInputRef.current?.click()
              }}
            >
              <i className="fa fa-upload mr-1" aria-hidden="true" />
              Import
            </button>
            <button
              type="button"
              className="btn btn-sm table-editor__io table-editor__io--csv"
              onPointerDown={stop}
              onClick={e => {
                stop(e)
                downloadTableSpreadsheetCsv(
                  rows,
                  columns,
                  cells,
                  props.getExportFilename()
                )
              }}
            >
              <i className="fa fa-download mr-1" aria-hidden="true" />
              CSV
            </button>
            <button
              type="button"
              className="btn btn-sm table-editor__io table-editor__io--xlsx"
              onPointerDown={stop}
              onClick={e => {
                stop(e)
                downloadTableSpreadsheetXlsx(
                  rows,
                  columns,
                  cells,
                  props.getExportFilename()
                )
              }}
            >
              <i className="fa fa-download mr-1" aria-hidden="true" />
              XLSX
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="table-editor__file"
              onChange={handleImportFile}
              onPointerDown={stop}
            />
            <button
              type="button"
              className="btn btn-sm btn-secondary table-editor__add"
              onPointerDown={stop}
              onClick={e => {
                stop(e)
                props.addRow()
                forceUpdate()
              }}
            >
              <i className="fa fa-plus mr-1" aria-hidden="true" />
              Row
            </button>
            {!columnsLocked && (
              <button
                type="button"
                className="btn btn-sm btn-secondary table-editor__add"
                onPointerDown={stop}
                onClick={e => {
                  stop(e)
                  props.addColumn()
                  forceUpdate()
                }}
              >
                <i className="fa fa-plus mr-1" aria-hidden="true" />
                Column
              </button>
            )}
          </div>

          {columnsLocked && (
            <p className="table-editor__locked-note">
              <i className="fa fa-link mr-1" aria-hidden="true" />
              Columns are set by the connected reference dataset.
            </p>
          )}

          {rows.length === 0 && columns.length === 0 ? (
            <p className="table-editor__empty">
              {columnsLocked
                ? "Add a row — columns come from the reference dataset once the model runs."
                : "Empty table — add a row and a column to begin."}
            </p>
          ) : (
            <div className="table-editor__scroll">
              <table
                className="table-editor__table"
                onMouseLeave={() => setHoverCell(null)}
              >
                <colgroup>
                  <col className="table-editor__col-row" />
                  {columns.map((_col, c) => (
                    <col key={`col-width-${c}`} className="table-editor__col-data" />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th className="table-editor__corner" aria-hidden />
                    {columns.map((col, c) => (
                      <th
                        key={`col-${c}`}
                        className={`table-editor__colhead${
                          hoverCell?.c === c ? " table-editor__colhead--highlight" : ""
                        }`}
                      >
                        {columnsLocked ? (
                          <span className="table-editor__head-locked" title={col}>
                            <span className="table-editor__head-locked-text">{col}</span>
                          </span>
                        ) : (
                          <div className="table-editor__head-wrap">
                            <input
                              className="form-control form-control-sm table-editor__head-input"
                              value={col}
                              onChange={e => {
                                props.setColumnName(c, e.target.value)
                                forceUpdate()
                              }}
                              onPointerDown={stop}
                              onDoubleClick={stop}
                              aria-label={`Column ${c + 1} name`}
                            />
                            <button
                              type="button"
                              className="table-editor__remove"
                              title="Remove column"
                              aria-label="Remove column"
                              onPointerDown={stop}
                              onClick={e => {
                                stop(e)
                                props.removeColumn(c)
                                forceUpdate()
                              }}
                            >
                              <i className="fa fa-times" aria-hidden="true" />
                            </button>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, r) => (
                    <tr key={`row-${r}`}>
                      <th
                        className={`table-editor__rowhead${
                          hoverCell?.r === r ? " table-editor__rowhead--highlight" : ""
                        }`}
                      >
                        <div className="table-editor__head-wrap">
                          <input
                            className="form-control form-control-sm table-editor__head-input"
                            value={row}
                            onChange={e => {
                              props.setRowName(r, e.target.value)
                              forceUpdate()
                            }}
                            onPointerDown={stop}
                            onDoubleClick={stop}
                            aria-label={`Row ${r + 1} name`}
                          />
                          <button
                            type="button"
                            className="table-editor__remove"
                            title="Remove row"
                            aria-label="Remove row"
                            onPointerDown={stop}
                            onClick={e => {
                              stop(e)
                              props.removeRow(r)
                              forceUpdate()
                            }}
                          >
                            <i className="fa fa-times" aria-hidden="true" />
                          </button>
                        </div>
                      </th>
                      {columns.map((_col, c) => (
                        <td
                          key={`cell-${r}-${c}`}
                          className={`table-editor__cell${
                            isRowHighlightCell(r, c) ? " table-editor__cell--highlight-row" : ""
                          }${isColHighlightCell(r, c) ? " table-editor__cell--highlight-col" : ""}`}
                          onMouseEnter={() => setHoverCell({ r, c })}
                        >
                          <input
                            type="number"
                            step="any"
                            className="table-editor__cell-input"
                            value={cellToString(cells[r] ? cells[r][c] : null)}
                            onChange={e => {
                              props.setCell(r, c, e.target.value)
                              forceUpdate()
                            }}
                            onPointerDown={stop}
                            onDoubleClick={stop}
                            aria-label={`Cell ${r + 1}, ${c + 1}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export class TableEditorControl extends Control {
  props: TableEditorProps
  component: (props: TableEditorProps) => JSX.Element
  type = "TableEditorControl"

  constructor(
    emitter: Emitter<EventsTypes> | null,
    controlKey = "tableEditor",
    columnsLockInputKey?: string
  ) {
    super(controlKey)
    const process = debounce(() => emitter?.trigger("process"), 400)

    const data = () => this.getNode().data as Record<string, unknown>

    const readRows = (): string[] => {
      const v = data()[TABLE_ROWS_KEY]
      return Array.isArray(v) ? (v as string[]) : []
    }
    const readColumns = (): string[] => {
      const v = data()[TABLE_COLUMNS_KEY]
      return Array.isArray(v) ? (v as string[]) : []
    }
    const readCells = (): TableCell[][] => {
      const rows = readRows()
      const columns = readColumns()
      const raw = data()[TABLE_CELLS_KEY]
      const existing = Array.isArray(raw) ? (raw as TableCell[][]) : []
      // Normalise the stored grid to the current row/column shape so we never
      // index out of bounds after structural edits.
      return rows.map((_r, r) =>
        columns.map((_c, c) => {
          const value = existing[r] ? existing[r][c] : null
          return typeof value === "number" && Number.isFinite(value) ? value : null
        })
      )
    }

    this.props = {
      getRows: readRows,
      getColumns: readColumns,
      getCells: readCells,
      getOpen: () => data()[TABLE_OPEN_KEY] === true,
      setOpen: (open: boolean) => {
        data()[TABLE_OPEN_KEY] = open
      },
      getColumnsLocked: () => {
        if (!columnsLockInputKey) return false
        const input = this.getNode().inputs.get(columnsLockInputKey)
        return !!input && input.connections.length > 0
      },
      setRowName: (index: number, name: string) => {
        const rows = [...readRows()]
        if (index < 0 || index >= rows.length) return
        rows[index] = name
        this.putData(TABLE_ROWS_KEY, rows)
        process()
      },
      setColumnName: (index: number, name: string) => {
        const columns = [...readColumns()]
        if (index < 0 || index >= columns.length) return
        columns[index] = name
        this.putData(TABLE_COLUMNS_KEY, columns)
        process()
      },
      setCell: (rowIndex: number, columnIndex: number, value: string) => {
        const cells = readCells()
        if (!cells[rowIndex]) return
        const trimmed = value.trim()
        const parsed = trimmed === "" ? null : Number(trimmed)
        cells[rowIndex][columnIndex] = parsed === null || Number.isNaN(parsed) ? null : parsed
        this.putData(TABLE_CELLS_KEY, cells)
        process()
      },
      addRow: () => {
        const rows = [...readRows()]
        const columns = readColumns()
        const cells = readCells()
        rows.push(`Row ${rows.length + 1}`)
        cells.push(new Array<TableCell>(columns.length).fill(null))
        this.putData(TABLE_ROWS_KEY, rows)
        this.putData(TABLE_CELLS_KEY, cells)
        process()
      },
      addColumn: () => {
        const columns = [...readColumns()]
        const cells = readCells()
        columns.push(`Column ${columns.length + 1}`)
        cells.forEach(row => row.push(null))
        this.putData(TABLE_COLUMNS_KEY, columns)
        this.putData(TABLE_CELLS_KEY, cells)
        process()
      },
      removeRow: (index: number) => {
        const rows = [...readRows()]
        const cells = readCells()
        if (index < 0 || index >= rows.length) return
        rows.splice(index, 1)
        cells.splice(index, 1)
        this.putData(TABLE_ROWS_KEY, rows)
        this.putData(TABLE_CELLS_KEY, cells)
        process()
      },
      removeColumn: (index: number) => {
        const columns = [...readColumns()]
        const cells = readCells()
        if (index < 0 || index >= columns.length) return
        columns.splice(index, 1)
        cells.forEach(row => row.splice(index, 1))
        this.putData(TABLE_COLUMNS_KEY, columns)
        this.putData(TABLE_CELLS_KEY, cells)
        process()
      },
      importTable: (imported: TableSpreadsheetData) => {
        const locked = columnsLockInputKey
          ? (() => {
              const input = this.getNode().inputs.get(columnsLockInputKey)
              return !!input && input.connections.length > 0 ? readColumns() : null
            })()
          : null
        const merged = mergeImportedTable(imported, locked)
        this.putData(TABLE_ROWS_KEY, merged.rows)
        this.putData(TABLE_COLUMNS_KEY, merged.columns)
        this.putData(TABLE_CELLS_KEY, merged.cells)
        process()
      },
      getExportFilename: () => {
        const name = this.getNode().data.name
        if (typeof name === "string" && name.trim() !== "") {
          return name.trim()
        }
        return "table"
      },
    }
    this.component = TableEditorView
  }
}
